/**
 * J.A.R.V.I.S.-style ARIA boot experience.
 *
 * Flow:
 *  1) FIRST EVER VISIT (no operator name) — full-screen IDENTIFY OPERATOR splash,
 *     terminal-style name capture, then cinematic boot + TTS greeting.
 *  2) SUBSEQUENT VISITS — invisible. First click ANYWHERE on the site fires a
 *     compact ARIA re-engage HUD with a TTS welcome-back. Only once per session.
 *  3) A persistent bottom-right ARIA summon button to manually re-trigger.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { AriaOrb } from "./AriaOrb";
import useWakeWord from "../hooks/useWakeWord";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NAME_KEY = "aria_operator_name";
const SESSION_KEY = "aria_jarvis_session_armed";
const WAKE_KEY = "aria_wake_enabled";

const BOOT_LINES = [
  { t: 80,   text: "▸ initializing aria.core …",            ok: "ok" },
  { t: 260,  text: "▸ loading knowledge graph · connected sources", ok: "ok" },
  { t: 460,  text: "▸ handshake :: claude-haiku-4.5",        ok: "linked" },
  { t: 680,  text: "▸ audio pipeline :: openai · whisper",   ok: "online" },
  { t: 880,  text: "▸ neural envelope warm-up",              ok: "stable" },
  { t: 1080, text: "▸ privacy & memory guardrails",          ok: "armed" },
  { t: 1260, text: "▸ identifying operator",                 ok: "match" },
  { t: 1500, text: "▸ session keys rotated · ed25519",       ok: "ok" },
];

// ----------------------------------------------------------------------------
// WebAudio power-up sweep (no asset). 2 short, layered tones.
// ----------------------------------------------------------------------------
function playBootSweep() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const now = ctx.currentTime;

    // Sweep oscillator
    const osc = ctx.createOscillator();
    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(120, now);
    osc.frequency.exponentialRampToValueAtTime(880, now + 1.2);

    // Sub
    const sub = ctx.createOscillator();
    sub.type = "sine";
    sub.frequency.setValueAtTime(60, now);
    sub.frequency.exponentialRampToValueAtTime(180, now + 1.2);

    // Filter (sweep up)
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.Q.value = 8;
    filter.frequency.setValueAtTime(200, now);
    filter.frequency.exponentialRampToValueAtTime(4000, now + 1.1);

    // Master gain envelope
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.18, now + 0.08);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 1.4);

    osc.connect(filter); sub.connect(filter); filter.connect(gain); gain.connect(ctx.destination);
    osc.start(now); sub.start(now);
    osc.stop(now + 1.4); sub.stop(now + 1.4);

    // Confirmation chime at the end
    const chime = ctx.createOscillator();
    chime.type = "triangle";
    chime.frequency.setValueAtTime(1175, now + 1.05);  // D6
    const cg = ctx.createGain();
    cg.gain.setValueAtTime(0.0001, now + 1.05);
    cg.gain.exponentialRampToValueAtTime(0.10, now + 1.10);
    cg.gain.exponentialRampToValueAtTime(0.0001, now + 1.55);
    chime.connect(cg); cg.connect(ctx.destination);
    chime.start(now + 1.05); chime.stop(now + 1.55);
  } catch (_) { /* WebAudio unsupported (older Safari/Firefox) — boot is silent, no UX impact */ }
}

function playClickPulse() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const now = ctx.currentTime;
    const o = ctx.createOscillator();
    o.type = "sine";
    o.frequency.setValueAtTime(880, now);
    o.frequency.exponentialRampToValueAtTime(220, now + 0.18);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(0.10, now + 0.03);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);
    o.connect(g); g.connect(ctx.destination);
    o.start(now); o.stop(now + 0.24);
  } catch (_) { /* WebAudio unsupported — pulse is silent, no UX impact */ }
}

// ----------------------------------------------------------------------------
// Expanding magenta ring + scan emitted from a screen point — pure CSS
// ----------------------------------------------------------------------------
function ClickRing({ x, y, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 1100);
    return () => clearTimeout(t);
  }, [onDone]);
  return (
    <div
      className="pointer-events-none fixed z-[100]"
      style={{ left: x, top: y, transform: "translate(-50%, -50%)" }}
      aria-hidden
    >
      <div className="jarvis-ring jarvis-ring-1" />
      <div className="jarvis-ring jarvis-ring-2" />
      <div className="jarvis-ring jarvis-ring-3" />
    </div>
  );
}

// ----------------------------------------------------------------------------
// Boot overlay — full-screen CRT scanlines + grid + identify/boot terminal
// ----------------------------------------------------------------------------
function BootOverlay({ phase, name, setName, onSubmitName, bootStep, onSkip, onDismiss, briefing }) {
  const inputRef = useRef(null);
  useEffect(() => {
    if (phase === "identify" && inputRef.current) inputRef.current.focus();
  }, [phase]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") (phase === "booting" ? onDismiss : onSkip)?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase, onDismiss, onSkip]);

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-[#030507] text-white overflow-hidden font-mono" data-testid="jarvis-boot-overlay">
      {/* CRT scanlines */}
      <div className="absolute inset-0 pointer-events-none jarvis-scanlines" />
      {/* Grid */}
      <div className="absolute inset-0 pointer-events-none jarvis-grid" />
      {/* Vignette */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: "radial-gradient(circle at 50% 50%, transparent 0%, rgba(3,5,7,0.95) 90%)"
      }} />
      {/* Sweeping scan beam */}
      <div className="absolute inset-0 pointer-events-none jarvis-scanbeam" />

      {/* HUD corners */}
      <div className="absolute top-6 left-6 text-[10px] tracking-[0.28em] text-[var(--aria-primary)] opacity-80">
        <div>ARIA · v2026.09</div>
        <div className="text-white/40 mt-1">ELECTRIC MIDNIGHT</div>
      </div>
      <div className="absolute top-6 right-6 text-[10px] tracking-[0.28em] text-right">
        <div className="text-[var(--aria-accent)] opacity-80">STATUS · COLD-BOOT</div>
        <div className="text-white/40 mt-1">SECURE CHANNEL · TLS 1.3</div>
      </div>
      <div className="absolute bottom-6 left-6 text-[10px] tracking-[0.28em] text-white/40">
        PRIVATE ACADEMIC INTELLIGENCE SYSTEM
      </div>
      <div className="absolute bottom-6 right-6 text-[10px] tracking-[0.28em] text-white/40">
        © EMERGENT LABS
      </div>

      {/* Central content */}
      <div className="relative z-10 flex flex-col items-center max-w-3xl px-6 w-full">
        <div className="mb-8 relative">
          <AriaOrb size={120} state={phase === "booting" ? "thinking" : "idle"} />
          <div className="absolute -inset-12 rounded-full border border-[var(--aria-primary)]/30 animate-[jarvis-spin_8s_linear_infinite]" />
          <div className="absolute -inset-20 rounded-full border border-[var(--aria-accent)]/20 animate-[jarvis-spin_14s_linear_infinite_reverse]" />
        </div>

        {phase === "identify" && (
          <form onSubmit={(e) => { e.preventDefault(); onSubmitName(); }} className="w-full max-w-lg text-center">
            <div className="text-[10px] tracking-[0.32em] text-[var(--aria-primary)] mb-2">[ ARIA · COLD START ]</div>
            <div className="text-4xl md:text-5xl font-display tracking-tight mb-1 text-grad-brand jarvis-glitch" data-text="IDENTIFY OPERATOR">
              IDENTIFY OPERATOR
            </div>
            <div className="text-xs text-white/50 mb-8 mt-3">
              Authentication of identity required to initialize ARIA. Provide name to proceed.
            </div>
            <label className="block text-left">
              <span className="text-[10px] tracking-[0.32em] text-[var(--aria-accent)]">▸ ENTER OPERATOR NAME</span>
              <div className="mt-2 flex items-center gap-3 border-b border-[var(--aria-primary)]/60 pb-2 focus-within:border-[var(--aria-primary)] transition-colors">
                <span className="text-[var(--aria-primary)]">&gt;</span>
                <input
                  ref={inputRef}
                  data-testid="jarvis-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={32}
                  placeholder="e.g. Shankar"
                  className="flex-1 bg-transparent text-2xl outline-none text-white placeholder-white/20 tracking-tight font-display"
                />
                <span className="jarvis-caret" />
              </div>
            </label>
            <div className="mt-8 flex items-center justify-between text-[11px]">
              <button
                type="button"
                onClick={onSkip}
                className="text-white/40 hover:text-white/80 tracking-[0.18em] uppercase"
                data-testid="jarvis-skip"
              >
                skip identification
              </button>
              <button
                type="submit"
                disabled={!name.trim()}
                data-testid="jarvis-submit"
                className="px-5 py-2 border border-[var(--aria-primary)] text-[var(--aria-primary)] hover:bg-[var(--aria-primary)] hover:text-white transition-colors tracking-[0.2em] uppercase disabled:opacity-30"
              >
                engage ▸
              </button>
            </div>
          </form>
        )}

        {phase === "booting" && (
          <div className="w-full max-w-2xl" data-testid="jarvis-booting">
            <div className="text-[10px] tracking-[0.32em] text-[var(--aria-primary)] mb-2 text-center">[ ARIA · INITIALIZING ]</div>
            <div className="text-3xl md:text-4xl font-display tracking-tight mb-2 text-center text-grad-brand jarvis-glitch" data-text={`HELLO, DR. ${(localStorage.getItem(NAME_KEY) || "").toUpperCase() || "OPERATOR"}.`}>
              HELLO, DR. {(localStorage.getItem(NAME_KEY) || "").toUpperCase() || "OPERATOR"}.
            </div>
            {/* Personalised briefing line (when authenticated) */}
            {briefing && (
              <div className="mt-1 mb-6 text-center text-sm md:text-base text-white/70 italic max-w-xl mx-auto jarvis-bootline" data-testid="jarvis-briefing">
                {briefing}
              </div>
            )}
            {!briefing && <div className="mb-6" />}
            <div className="space-y-1 text-[11px] leading-relaxed">
              {BOOT_LINES.slice(0, bootStep).map((ln) => (
                <div key={ln.text} className="flex justify-between text-white/70 jarvis-bootline">
                  <span>{ln.text}</span>
                  <span className="text-[var(--aria-primary)] ml-3">[ {ln.ok} ]</span>
                </div>
              ))}
            </div>
            <div className="mt-6 h-[2px] bg-white/5 overflow-hidden">
              <div
                className="h-full transition-[width] duration-200 ease-out"
                style={{
                  width: `${Math.min(100, (bootStep / BOOT_LINES.length) * 100)}%`,
                  background: "var(--aria-grad-brand)",
                  boxShadow: "0 0 18px var(--aria-primary)",
                }}
              />
            </div>
            <div className="mt-3 text-[10px] tracking-[0.32em] text-white/40 text-center">
              {bootStep < BOOT_LINES.length ? "compiling …" : "ready"}
            </div>
            <div className="mt-5 text-center">
              <button type="button" onClick={onDismiss} data-testid="jarvis-boot-dismiss"
                className="text-[10px] text-white/35 hover:text-white/80 tracking-[0.22em] uppercase">
                skip boot · esc
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Main JarvisBoot
// ----------------------------------------------------------------------------
export default function JarvisBoot() {
  const location = useLocation();
  const [phase, setPhase] = useState("idle");            // idle | identify | booting
  const [name, setName] = useState("");
  const [bootStep, setBootStep] = useState(0);
  const [rings, setRings] = useState([]);                // {id,x,y}
  const [briefing, setBriefing] = useState(null);        // personalised line from /api/analytics/briefing
  const [wakeEnabled, setWakeEnabled] = useState(() => localStorage.getItem(WAKE_KEY) === "1");
  const audioRef = useRef(null);

  // Don't show splash on protected SSO simulator
  const blockedRoute = location.pathname.startsWith("/sso-idp") || location.pathname.startsWith("/advisor/");

  // Wake-word listener — pauses during boot/identify phase to avoid self-triggers from TTS
  const { supported: wakeSupported, active: wakeActive, error: wakeError } = useWakeWord({
    enabled: wakeEnabled && phase === "idle" && !blockedRoute,
    onWake: () => {
      const stored = localStorage.getItem(NAME_KEY) || "Operator";
      // Visual flourish from screen center
      setRings((r) => [...r, {
        id: Math.random(),
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
      }]);
      playClickPulse();
      setTimeout(() => triggerBoot(stored, true), 220);
    },
  });

  const toggleWake = () => {
    setWakeEnabled((cur) => {
      const next = !cur;
      localStorage.setItem(WAKE_KEY, next ? "1" : "0");
      return next;
    });
  };

  // Cold-boot trigger: first visit ever
  useEffect(() => {
    if (blockedRoute) return;
    const stored = localStorage.getItem(NAME_KEY);
    if (!stored) {
      const t = setTimeout(() => setPhase("identify"), 350);
      return () => clearTimeout(t);
    }
  }, [blockedRoute]);

  // Returning visit: arm any-click handler ONCE per session
  useEffect(() => {
    if (blockedRoute) return;
    const stored = localStorage.getItem(NAME_KEY);
    if (!stored) return;
    if (sessionStorage.getItem(SESSION_KEY) === "1") return;

    const handler = (e) => {
      // Ignore clicks on the boot overlay itself and on the floating summon button
      if (e.target.closest?.("[data-testid='jarvis-boot-overlay']")) return;
      if (e.target.closest?.("[data-testid='jarvis-summon']")) return;
      if (sessionStorage.getItem(SESSION_KEY) === "1") return;
      sessionStorage.setItem(SESSION_KEY, "1");
      document.removeEventListener("click", handler, true);
      // Ring at click coords, then full re-engage HUD
      const id = Math.random();
      setRings((r) => [...r, { id, x: e.clientX, y: e.clientY }]);
      playClickPulse();
      setTimeout(() => triggerBoot(stored, true), 220);
    };
    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockedRoute, location.pathname]);

  const playGreeting = useCallback(async (operator, returning) => {
    try {
      const res = await fetch(`${API_BASE}/voice/welcome`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: operator, returning }),
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (!audioRef.current) audioRef.current = new Audio();
      audioRef.current.src = url;
      audioRef.current.volume = 0.85;
      audioRef.current.play().catch((e) => console.info("[jarvis] autoplay blocked (expected pre-interaction):", e?.name));
    } catch (e) { console.warn("[jarvis] welcome TTS failed:", e?.message || e); }
  }, []);

  const runBootSequence = useCallback((operator, returning) => {
    // Reset cleanly so re-triggers work after a prior completion
    setPhase("booting");
    setBootStep(0);
    setBriefing(null);
    playBootSweep();
    playGreeting(operator, returning);

    // Fetch personalised briefing (best-effort; 401 for anon users resolves to null)
    fetch(`${API_BASE}/analytics/briefing`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.line) setBriefing(d.line); })
      .catch((e) => console.info("[jarvis] briefing skipped:", e?.message || e));

    let i = 0;
    const tick = () => {
      i += 1;
      setBootStep(i);
      if (i < BOOT_LINES.length) {
        setTimeout(tick, BOOT_LINES[i]?.t ? Math.max(120, BOOT_LINES[i].t - BOOT_LINES[i - 1].t) : 200);
      } else {
        // Done — fade out, route logic.
        // Boot never auto-opens Conversation Mode — voice is launched explicitly
        // (mic button, "Hey ARIA" wake word, or the summon orb).
        setTimeout(() => setPhase("idle"), 1100);
      }
    };
    setTimeout(tick, 150);
  }, [playGreeting]);

  const triggerBoot = useCallback((operator, returning) => {
    runBootSequence(operator, returning);
  }, [runBootSequence]);

  const handleSubmitName = () => {
    const clean = name.trim().slice(0, 32);
    if (!clean) return;
    localStorage.setItem(NAME_KEY, clean);
    sessionStorage.setItem(SESSION_KEY, "1"); // we've greeted them on cold boot
    triggerBoot(clean, false);
  };

  const handleSkip = () => {
    localStorage.setItem(NAME_KEY, "Researcher");
    sessionStorage.setItem(SESSION_KEY, "1");
    setPhase("idle");
  };

  const handleDismissBoot = () => {
    if (audioRef.current) { audioRef.current.pause(); }
    setBootStep(BOOT_LINES.length);
    setPhase("idle");
  };

  // Manual re-summon button: re-runs boot with stored name
  const handleSummon = () => {
    const stored = localStorage.getItem(NAME_KEY);
    if (!stored) { setPhase("identify"); return; }
    triggerBoot(stored, true);
  };

  return (
    <>
      {/* Floating ARIA summon (always available, hidden during boot) */}
      {phase === "idle" && !blockedRoute && (
        <div className="fixed bottom-6 left-6 z-[150] flex items-center gap-2">
          <button
            onClick={handleSummon}
            data-testid="jarvis-summon"
            className="group flex items-center gap-2 pl-2 pr-4 py-2 rounded-full bg-[#030507]/85 border border-[var(--aria-primary)]/50 backdrop-blur-sm hover:border-[var(--aria-primary)] transition-all hover:pl-3 hover:pr-5"
            aria-label="Summon ARIA"
          >
            <AriaOrb size={28} state={wakeActive ? "listening" : "idle"} />
            <span className="text-[10px] tracking-[0.28em] uppercase text-[var(--aria-primary)] group-hover:text-white transition-colors font-mono">
              Hey, ARIA
            </span>
          </button>

          {/* Wake-word mic toggle */}
          {wakeSupported && (
            <button
              onClick={toggleWake}
              data-testid="jarvis-wake-toggle"
              title={wakeError ? wakeError : wakeEnabled
                ? `Wake-word ON — say "Hey ARIA" anywhere on the page`
                : `Wake-word OFF — click to enable hands-free "Hey ARIA"`}
              className={`relative w-9 h-9 rounded-full backdrop-blur-sm border transition-all flex items-center justify-center ${
                wakeEnabled
                  ? "bg-[var(--aria-primary)]/15 border-[var(--aria-primary)] text-[var(--aria-primary)]"
                  : "bg-[#030507]/85 border-[var(--aria-border-strong)] text-[var(--aria-text-muted)] hover:border-[var(--aria-primary)] hover:text-[var(--aria-primary)]"
              }`}
              aria-label={wakeEnabled ? "Disable wake-word" : "Enable wake-word"}
              aria-pressed={wakeEnabled}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                <line x1="12" y1="19" x2="12" y2="22" />
              </svg>
              {wakeActive && (
                <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-[var(--aria-primary)] animate-pulse shadow-[0_0_8px_var(--aria-primary)]" />
              )}
            </button>
          )}
        </div>
      )}

      {/* Click rings */}
      {rings.map((r) => (
        <ClickRing key={r.id} x={r.x} y={r.y}
          onDone={() => setRings((rs) => rs.filter((x) => x.id !== r.id))}
        />
      ))}

      {/* Boot / identify overlay */}
      {phase !== "idle" && (
        <BootOverlay
          phase={phase}
          name={name}
          setName={setName}
          onSubmitName={handleSubmitName}
          onSkip={handleSkip}
          onDismiss={handleDismissBoot}
          bootStep={bootStep}
          briefing={briefing}
        />
      )}
    </>
  );
}
